import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { useNavigate } from 'react-router'
import { useSetAtom } from 'jotai'
import { useState, useMemo, useEffect, useRef } from 'react'
import { setCartItemsAtom, TicketItem, CartItem } from '@/core/store/cart.store'
import { setCustomerDataAtom, clearCustomerAtom } from '@/core/store/customer.store'
import { HeroSection, TitleSection, TicketCard } from '@/core/components/home'
import { TicketCategory } from '@/core/components/home/ticket.types'
import {
    getTicketsWithStatus,
    getCategoryDisplayTickets,
    getTicketsByCategory,
    isCategoryEligible,
    getDefaultCategory,
    getEffectivePrice,
    isEligibleForEarlyBirdPrice,
    parseEndDate
} from '@/core/components/home/ticket.utils'
import { useTicketOptions } from '@/core/hooks/useTicketOptions'
import IntroduceGlic from '@/core/components/home/IntroduceGlic'
import WhyAttend from '@/core/components/home/WhyAttend'
import TicketIncluded from '@/core/components/home/TicketIncluded'
import EventDetails from '@/core/components/home/EventDetails'
import PhotoGallery from '@/core/components/home/PhotoGallery'
import FAQ from '@/core/components/home/FAQ'
import CustomerIdModal from '@/core/components/home/CustomerIdModal'
import { useTranslate } from '@repo/unicity'
import type { TTinyCustomer } from '@/core/types'

const HomePage = () => {
    const navigate = useNavigate()
    const setCartItems = useSetAtom(setCartItemsAtom)
    const setCustomerData = useSetAtom(setCustomerDataAtom)
    const clearCustomer = useSetAtom(clearCustomerAtom)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const { translate } = useTranslate()
    const { tickets: ticketOptions, isLoading: isLoadingTickets } = useTicketOptions()
    const hasSetCategory = useRef(false)

    // Get tickets with computed status based on current date
    const ticketsWithStatus = useMemo(() => getTicketsWithStatus(ticketOptions), [ticketOptions])

    // Get display tickets (one per category)
    const displayTickets = useMemo(() => getCategoryDisplayTickets(ticketsWithStatus), [ticketsWithStatus])

    // Get default category
    const defaultCategory = useMemo(() => getDefaultCategory(ticketsWithStatus), [ticketsWithStatus])

    const [selectedCategory, setSelectedCategory] = useState<TicketCategory>('early_bird')

    // Sync selected category with default once tickets load
    useEffect(() => {
        if (defaultCategory && !hasSetCategory.current && ticketOptions.length > 0) {
            setSelectedCategory(defaultCategory)
            hasSetCategory.current = true
        }
    }, [defaultCategory, ticketOptions])

    // Handle category selection (from TicketCard)
    const handleCategorySelect = (itemCode: string) => {
        const ticket = ticketsWithStatus.find(t => t.item_code === itemCode)
        if (!ticket) return

        // Set the selected category
        setSelectedCategory(ticket.category)
    }

    const handleBuyTicket = () => {
        // Clear existing customer data and always open modal for fresh input
        clearCustomer()
        setIsModalOpen(true)
    }

    const handleCustomerSubmit = (customer: TTinyCustomer) => {
        // Customer eligibility is already checked in CustomerIdModal
        // Check if customer is eligible for early bird pricing
        const selectedTicket = ticketsWithStatus.find(t => t.category === selectedCategory)
        const isEarlyBirdEligible =
            selectedTicket && selectedCategory !== 'early_bird' && isEligibleForEarlyBirdPrice(selectedTicket, customer.isCanBuyEarlyBird)

        // Determine which category to use for cart
        const effectiveCategory = isEarlyBirdEligible ? 'early_bird' : selectedCategory

        // Get all tickets in the effective category
        let categoryTickets = getTicketsByCategory(ticketsWithStatus, effectiveCategory)

        // If using early_bird category (either selected or due to eligibility),
        // filter out 10+1 ticket if its period has passed
        if (effectiveCategory === 'early_bird') {
            const now = new Date()
            categoryTickets = categoryTickets.filter(ticket => {
                // If this is the 10+1 ticket, check its period
                if (ticket.ticket_type === 'glic_ticket_earlybird_group_period') {
                    const periodEnd = parseEndDate(ticket.period_end)
                    return now <= periodEnd
                }
                return true
            })
        }

        if (categoryTickets.length === 0) return

        // Create cart items based on category type
        const cartItems: CartItem[] = categoryTickets.map(ticket => {
            const effectivePrice = getEffectivePrice(ticket, customer.isCanBuyEarlyBird)
            const ticketItem: TicketItem = {
                id: ticket.item_code,
                name: ticket.item_name,
                price: effectivePrice,
                category: ticket.ticket_type,
                date: ticket.period_text
            }

            // USHOP-2914: early bird for a customer who can buy it → fixed at 1 (locked, no stepper).
            // Early bird otherwise → qty 0 (user sets later). Other categories → qty 1.
            const isEarlyBird = effectiveCategory === 'early_bird'
            const locked = isEarlyBird && !!customer.isCanBuyEarlyBird
            let quantity = 1
            if (isEarlyBird) quantity = locked ? 1 : 0

            return { ticket: ticketItem, quantity, locked }
        })

        // Save customer data to store
        setCustomerData(customer)

        // Set cart items directly
        setCartItems(cartItems)
        setIsModalOpen(false)
        navigate('/order-detail')
    }

    return (
        <Stack sx={{ flex: 1, bgcolor: 'rgba(204, 222, 239, 1)' }}>
            {/* Hero Image */}
            <HeroSection />

            {/* Content */}
            <Stack sx={{ padding: '48px 24px', gap: 3 }}>
                {/* Title Section */}
                <TitleSection />

                {/* Ticket Options (one per category) */}
                <Stack gap={3}>
                    {isLoadingTickets && (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress size={40} sx={{ color: 'primary.main' }} />
                        </Stack>
                    )}
                    {!isLoadingTickets &&
                        displayTickets.map(ticket => (
                            <TicketCard
                                key={ticket.item_code}
                                ticket={ticket}
                                isSelected={selectedCategory === ticket.category}
                                onSelect={handleCategorySelect}
                                isEligible={isCategoryEligible(ticketsWithStatus, ticket.category)}
                            />
                        ))}
                </Stack>

                {/* Buy Button */}
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleBuyTicket}
                    sx={{
                        fontSize: '20px'
                    }}>
                    {translate('buy_ticket')}
                </Button>
            </Stack>
            <IntroduceGlic />

            <WhyAttend />
            <TicketIncluded />
            <EventDetails />
            <PhotoGallery />
            <FAQ />

            {/* Customer ID Modal */}
            <CustomerIdModal open={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={(customer: TTinyCustomer) => handleCustomerSubmit(customer)} />
        </Stack>
    )
}

export default HomePage
